import React, { useContext, useEffect } from "react";
import {
  AuthEventType,
  getAuthenticationContext,
  getIdToken,
} from "../../auth/AuthenticationContext";
import {
  getRecommendBrowserMessage,
  haveWebGl2Support,
  isSupportedBrowser,
} from "../../utils/browserUtils";
import getEnvConfig from "../../utils/config";
import { NOT_AUTHORIZED } from "../../utils/constants";
import {
  setPrincipalIdToPuEnv,
  setQueryStringsEnv,
} from "../../utils/environmentUtils";
import { isGameDown } from "../../utils/gameConfigUtils";
import { useMonitoring } from "../../utils/monitoring-context";
import { getQueryStrings, getWebGlLocation } from "../../utils/urlUtils";
import { AccessNotAuthorized } from "./AccessNotAuthorized";
import WebGL from "./WebGL";
import { SomethingWentWrong } from "../../components/SomethingWentWrong";
import {checkMigrationStatus} from "../../api/metadata";

/**
 * Possible scenarios
 *
 * - Unauthorized -> If the user fails auth or has no credentials
 * - LoadGame -> Prepare to load webgl
 * - LoadWebGL -> Loads the webgl component
 * - InvalidBrowser -> Invalid browser
 * - BrowserCompatibilityWarning -> Warn the user that the browser may not be compatible with the game being played
 * - Logout -> user clicked logout
 * - SystemIsDown -> Andon cord tells the system not to load
 */
const WINDOW_STATE = {
  Unauthorized: "Unauthorized",
  LoadGame: "LoadGame",
  LoadWebGL: "LoadWebGL",
  InvalidBrowser: "InvalidBrowser",
  BrowserCompatibilityWarning: "BrowserCompatibilityWarning",
  FailToCallBackend: "FailToCallBackend",
  SystemIsDown: "SystemIsDown",
  Logout: "Logout",
  TokenRenewFailure: "TokenRenewFailure",
};

export const Game = () => {
  const AuthenticationContext = useContext(getAuthenticationContext());

  const [windowState, setWindowState] = React.useState<string>(
    WINDOW_STATE.LoadGame
  );

  const [webglConfigLocation, setWebglConfigLocation] = React.useState<string>();

  const { updateMonitoringPrincipalId } = useMonitoring();

  const gameName = getEnvConfig().game_name;

  const loadGame = () => {
    const token = getIdToken();
    const standalone = getEnvConfig().standalone_webgl;
    const bootstrapCalls = standalone
      ? []
      : [setPrincipalIdToPuEnv(token), checkMigrationStatus(token)];
    Promise.all(bootstrapCalls)
      .then(() => getWebGlLocation(token))
      .then((webglConfigLocation) => {
        setWebglConfigLocation(webglConfigLocation)
        setWindowState(WINDOW_STATE.LoadWebGL);
        updateMonitoringPrincipalId(window.pu_environment?.principal_id);
      })
      .catch((err) => {
        if (err === NOT_AUTHORIZED) {
          setWindowState(WINDOW_STATE.Unauthorized);
        } else {
          console.log(err);
          setWindowState(WINDOW_STATE.FailToCallBackend);
        }
      });
  };

  const bootStrap = () => {
    if (isGameDown()) {
      setWindowState(WINDOW_STATE.SystemIsDown);
      return;
    }

    if (!haveWebGl2Support()) {
      console.log("Does not have webgl2 support");
      setWindowState(WINDOW_STATE.InvalidBrowser);
      return;
    } else {
      console.log("Have webgl support");
    }

    AuthenticationContext.checkUserLoggedIn()
      .then((_) => {
        if (!isSupportedBrowser()) {
          setWindowState(WINDOW_STATE.BrowserCompatibilityWarning);
        } else {
          loadGame();
        }
      })
      .catch((ex) => {
        console.error(ex);
        setWindowState(WINDOW_STATE.Unauthorized);
      });
  };

  useEffect(() => {
    document.title = gameName;
    bootStrap();
  }, []);

  useEffect(() => {
    const listener = (eventType: AuthEventType) => {
      if (eventType === AuthEventType.SIGN_OUT) {
        setWindowState(WINDOW_STATE.Logout);
      } else if (eventType === AuthEventType.UNAUTHORIZED) {
        setWindowState(WINDOW_STATE.Unauthorized);
      } else if (eventType === AuthEventType.TOKEN_REFRESH_ERROR) {
        setWindowState(WINDOW_STATE.TokenRenewFailure);
      } else if (eventType === AuthEventType.AUTH_SERVER_NOT_AVAILABLE) {
        setWindowState(WINDOW_STATE.FailToCallBackend);
      }
    };
    AuthenticationContext.addListener(listener);

    return () => {
      AuthenticationContext.removeListener(listener);
    };
  }, [AuthenticationContext]);

  console.log(windowState);
  setQueryStringsEnv(getQueryStrings());

  if (windowState === WINDOW_STATE.TokenRenewFailure) {
    // window.location will cause a browser redirect and this event to be logged at Cloud Front access log
    // Don't replace this by Components like <Navigate/> or <Link/> unless we don't want to
    // track this event at server side
    const win: Window = window;
    win.location = "/refresh-token";
    return <></>;
  }

  return (
    <>
      {windowState === WINDOW_STATE.InvalidBrowser && (
        <div className="cq-content">
          <p>Your browser does not support WebGL.</p>
          <p>This game requires WebGL2 to run properly. Please visit <a href="https://get.webgl.org/webgl2/" target="_blank" rel="noopener noreferrer">https://get.webgl.org/webgl2/</a> to learn more about WebGL2 support and how to enable it in your browser.</p>
          <div style={{ marginTop: "4rem" }}>
            <button
              type="button"
              onClick={AuthenticationContext.logout}
              className="cq-button-primary"
            >
              Logout
            </button>
          </div>
        </div>
      )}

      {windowState === WINDOW_STATE.FailToCallBackend && <SomethingWentWrong />}

      {windowState === WINDOW_STATE.SystemIsDown && (
        <div className="cq-content">
          <p style={{ fontSize: "8rem", lineHeight: "normal" }}>SORRY</p>
          <p>
            Something went wrong on our end. {gameName} is temporarily
            unavailable.
          </p>
        </div>
      )}
      {windowState === WINDOW_STATE.BrowserCompatibilityWarning && (
        <div className="cq-content invalid-browser">
          <p>
            We've detected you are using a browser that may not be compatible
            with {gameName}.
          </p>
          <p>{getRecommendBrowserMessage(gameName)}</p>
          <div style={{ marginTop: "4rem" }}>
            <button
              type="button"
              onClick={loadGame}
              className="cq-button-primary"
            >
              Proceed Anyway
            </button>
          </div>
        </div>
      )}
      {windowState === WINDOW_STATE.LoadGame && (
        <div className="cq-content">Loading Game</div>
      )}
      {windowState === WINDOW_STATE.Unauthorized && <AccessNotAuthorized />}
      {windowState === WINDOW_STATE.LoadWebGL && (
        <WebGL webGLConfig={webglConfigLocation} />
      )}
      {windowState === WINDOW_STATE.Logout && (
        <div className="cq-content">
          <p>You have logged out from {gameName}.</p>
          <div style={{ marginTop: "4rem" }}>
            <button
              type="button"
              onClick={() => {
                window.location.href = "https://explore.skillbuilder.aws/";
              }}
              className="cq-button-primary"
            >
              Go to AWS Skill Builder
            </button>
          </div>
        </div>
      )}
    </>
  );
};
